import type { SectionProps } from "../context";
import { domId } from "../context";
import { AttendanceChoice, FIELD, FormField, FormStatus, Honeypot } from "../forms";
import { eventInstant, parseEventDate } from "../helpers";
import { Icon } from "../icons";
import { BUTTON, Container, MultiLine, REVEAL, SectionHeader, SectionShell, cx } from "../ui";

/**
 * Attendance confirmation. One answer per name: answering again updates it. Live, the responses runtime
 * posts to /__baze/rsvp, prefills the name from ?to=, and hides "Jumlah orang" for "Tidak hadir". The form
 * closes after `deadline` (end of that day, WIB); the server render already shows it closed.
 */
export function Rsvp({ node, ctx }: SectionProps<"rsvp">) {
  const c = node.content;
  const inert = ctx.editor || ctx.thumbnail;
  const maxPax = c.maxPax ?? 2;
  const deadline = c.deadline ? parseEventDate(c.deadline) : null;
  const closesAt = c.deadline ? eventInstant(c.deadline, "23:59", "WIB") : null;
  const closed = !ctx.editor && closesAt !== null && ctx.now > closesAt + 59_000;
  const split = node.variant === "split";
  const intro = (
    <div className={cx(split ? "@3xl:pt-2" : "text-center")}>
      <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align={split ? "left" : "center"} />
      <MultiLine ctx={ctx} text={c.description} path="content.description" className={cx("mt-4 leading-relaxed text-site-muted", !split && "mx-auto max-w-xl")} />
      {deadline ? (
        <p className={cx("mt-4 inline-flex items-center gap-2 text-sm font-medium text-site-muted", !split && "justify-center")}>
          <Icon name="calendar" className="size-4" />
          <span>
            Mohon konfirmasi sebelum <time dateTime={c.deadline}>{deadline.long}</time>
          </span>
        </p>
      ) : null}
    </div>
  );
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size={split ? "default" : "narrow"}>
        <div className={cx(split ? "grid gap-10 @3xl:grid-cols-2 @3xl:gap-14" : "")}>
          {intro}
          <div className={cx(REVEAL, split ? "" : "mt-10")} data-baze-rsvp="" data-closes-at={closesAt ?? undefined}>
            <p data-baze-rsvp-closed="" hidden={!closed} className="rounded-site-lg border border-site-border bg-site-surface p-6 text-center font-medium text-site-muted">
              Konfirmasi kehadiran sudah ditutup. Terima kasih.
            </p>
            <form hidden={closed} className="relative space-y-5 rounded-site-lg border border-site-border bg-site-surface p-5 shadow-site-sm @md:p-7" data-baze-rsvp-form="" noValidate>
              <FormField ctx={ctx} id={`${node.id}-name`} label="Nama">
                <input id={domId(ctx, `${node.id}-name`)} name="name" required maxLength={60} autoComplete="name" disabled={inert} className={FIELD} placeholder="Nama sesuai undangan" />
              </FormField>
              <AttendanceChoice ctx={ctx} name="attending" idBase={`${node.id}-att`} required disabled={inert} />
              <div data-baze-pax="">
                <FormField ctx={ctx} id={`${node.id}-pax`} label="Jumlah orang (termasuk Anda)">
                  <select id={domId(ctx, `${node.id}-pax`)} name="pax" defaultValue="1" disabled={inert} className={cx(FIELD, "appearance-none")}>
                    {Array.from({ length: maxPax }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n} orang
                      </option>
                    ))}
                  </select>
                </FormField>
              </div>
              {c.askNote ? (
                <FormField ctx={ctx} id={`${node.id}-note`} label="Catatan (opsional)">
                  <textarea id={domId(ctx, `${node.id}-note`)} name="note" maxLength={300} rows={3} disabled={inert} className={cx(FIELD, "resize-y")} placeholder="Pesan untuk tuan rumah" />
                </FormField>
              ) : null}
              <Honeypot />
              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" disabled={inert} className={cx("baze-btn", BUTTON.primary, "disabled:cursor-not-allowed")}>
                  <Icon name="check" className="size-4" />
                  <span>Kirim konfirmasi</span>
                </button>
                <FormStatus />
              </div>
            </form>
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}
